// iter-107x: previous `eslint-disable react-hooks/immutability` directive
// removed — that rule isn't defined by eslint-plugin-react-hooks@5.2.0.
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, useAuth } from "../App";
import { useAdminControls } from "../AdminMode";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Calendar } from "../components/ui/calendar";
import { Checkbox } from "../components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import InvitedPlayersPicker from "../components/InvitedPlayersPicker";
import { Switch } from "../components/ui/switch";
import { format, startOfMonth, endOfMonth, isSameDay } from "date-fns";
import { toast } from "sonner";
import { 
  Plus, 
  Calendar as CalendarIcon, 
  MapPin, 
  Clock, 
  Trash2, 
  Edit,
  Music2,
  Filter,
  Bell,
  Repeat,
  UserCheck,
  AlertCircle,
  CheckCircle,
  XCircle,
  Upload,
  Download,
  Lock
} from "lucide-react";
import ImportDialog from "../components/ImportDialog";
import QuickRsvp from "../components/QuickRsvp";
import usePullToRefresh from "../hooks/usePullToRefresh";
import useScrollRestoration from "../hooks/useScrollRestoration";
import PullToRefreshIndicator from "../components/PullToRefreshIndicator";

const Events = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  // iter-86: preserve scroll position when navigating into an event and back
  useScrollRestoration({ ready: !loading });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [filterType, setFilterType] = useState("all");
  // iter-33: timeFilter — "upcoming" (default) or "past" (archive view)
  const [timeFilter, setTimeFilter] = useState("upcoming");
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    event_type: "rehearsal",
    location: "",
    arrival_time: "",
    start_time: "",
    end_time: "",
    arrival_tbc: false,
    start_tbc: false,
    end_tbc: false,
    status: "confirmed",
    reminder_enabled: false,
    reminder_hours: 24,
    reminder_offsets: [],  // iter-107k
    is_recurring: false,
    recurrence_pattern: "weekly",
    recurrence_count: 4,
    is_private: false,
    invited_player_ids: [],  // iter-159: only used for rehearsal_sectional
  });

  const isAdmin = user?.role === "admin";
  // Member-first view: hide admin controls unless admin has flipped the toggle.
  // showAdminControls === isAdmin && adminMode (see /src/AdminMode.jsx).
  const { showAdminControls } = useAdminControls();

  // iter-107k: catalogue for the multi-select reminder grid. Loaded once
  // per session — it's static config from the backend.
  const [reminderCatalogue, setReminderCatalogue] = useState([]);

  // iter-107m: "Send sample reminder to me" button state.
  const [sendingTestReminder, setSendingTestReminder] = useState(false);

  // iter-111: surface the current user's ``is_committee`` flag so we can
  // show/hide the Committee Meeting event_type option + filter pill.
  // Strictly committee-only per product decision — an admin who isn't
  // flagged committee won't see the option (server enforces this too).
  const [meIsCommittee, setMeIsCommittee] = useState(false);
  useEffect(() => {
    apiClient
      .get("/players/me/profile")
      .then((res) => setMeIsCommittee(!!res.data?.player?.is_committee))
      .catch(() => {});
  }, []);
  const canSeeCommitteeMeetings = meIsCommittee;

  useEffect(() => {
    fetchEvents();
  }, [filterType, timeFilter]);

  useEffect(() => {
    apiClient.get("/events/reminder-offsets")
      .then((res) => setReminderCatalogue(res.data?.options || []))
      .catch(() => {});
  }, []);

  // iter-107k: when admin picks a new event_type while creating a fresh
  // event, auto-tick the default offsets for that type. Skips editing
  // (existing offsets win) and skips if the admin has already manually
  // ticked something.
  useEffect(() => {
    if (editingEvent) return;
    if ((formData.reminder_offsets || []).length > 0) return;
    if (!formData.event_type) return;
    apiClient
      .get(`/events/reminder-offsets?event_type=${encodeURIComponent(formData.event_type)}`)
      .then((res) => {
        const defaults = res.data?.defaults_for || [];
        if (defaults.length > 0) {
          setFormData((prev) => (
            (prev.reminder_offsets || []).length > 0
              ? prev
              : { ...prev, reminder_offsets: defaults, reminder_enabled: true }
          ));
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.event_type, editingEvent]);


  const fetchEvents = async () => {
    try {
      const qs = new URLSearchParams();
      if (filterType !== "all") qs.set("event_type", filterType);
      qs.set("time_filter", timeFilter);
      const res = await apiClient.get(`/events?${qs.toString()}`);
      setEvents(res.data);
    } catch (error) {
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData };
      // Only include arrival_time if it's set
      if (!submitData.arrival_time) {
        delete submitData.arrival_time;
      }
      // iter-159: invited_player_ids is only meaningful for sectionals.
      if (submitData.event_type !== "rehearsal_sectional") {
        delete submitData.invited_player_ids;
      } else if (
        !submitData.invited_player_ids ||
        submitData.invited_player_ids.length === 0
      ) {
        toast.error("Add at least one player to invite to this sectional.");
        return;
      }
      // Don't send recurring fields when editing
      if (editingEvent) {
        delete submitData.is_recurring;
        delete submitData.recurrence_pattern;
        delete submitData.recurrence_count;
        await apiClient.put(`/events/${editingEvent.id}`, submitData);
        toast.success("Event updated successfully");
      } else {
        // Only include recurring fields if is_recurring is true
        if (!submitData.is_recurring) {
          delete submitData.recurrence_pattern;
          delete submitData.recurrence_count;
        }
        const res = await apiClient.post("/events", submitData);
        if (res.data.events) {
          toast.success(`Created ${res.data.events.length} recurring events!`);
        } else {
          toast.success("Event created successfully");
        }
      }
      setDialogOpen(false);
      setEditingEvent(null);
      resetForm();
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save event");
    }
  };

  const handleDelete = async (eventId) => {
    if (!confirm("Are you sure you want to delete this event?")) return;
    try {
      await apiClient.delete(`/events/${eventId}`);
      toast.success("Event deleted");
      fetchEvents();
    } catch (error) {
      toast.error("Failed to delete event");
    }
  };

  // Iter-66f → iter-66g: Quick RSVP handler moved into the shared
  // /components/QuickRsvp.jsx; parent updates local state via `onRsvpChange`.

  // iter-107m + iter-107o: fire a one-off sample reminder (email + push)
  // to the calling admin so they can preview both delivery channels
  // before relying on the scheduler. Members are NOT touched.
  const handleSendTestReminder = async () => {
    if (!editingEvent) return;
    setSendingTestReminder(true);
    try {
      const res = await apiClient.post(`/events/${editingEvent.id}/test-reminder`);
      const target = res.data?.sent_to || "your inbox";
      const pushMsg = res.data?.push_sent
        ? " + push"
        : res.data?.push_subscribed === false
          ? " (push skipped — enable web push on this device first)"
          : "";
      toast.success(`Sample reminder sent to ${target}${pushMsg}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to send test reminder";
      // iter-107o: a 412 means FROM_EMAIL is not a validated Brevo sender —
      // keep the toast visible longer because the message is actionable.
      const isSenderError = err?.response?.status === 412;
      toast.error(msg, { duration: isSenderError ? 12000 : 5000 });
    } finally {
      setSendingTestReminder(false);
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description || "",
      event_type: event.event_type,
      location: event.location,
      arrival_time: event.arrival_time ? new Date(event.arrival_time).toISOString().slice(0, 16) : "",
      start_time: new Date(event.start_time).toISOString().slice(0, 16),
      end_time: new Date(event.end_time).toISOString().slice(0, 16),
      arrival_tbc: event.arrival_tbc || false,
      start_tbc: event.start_tbc || false,
      end_tbc: event.end_tbc || false,
      status: event.status || "confirmed",
      reminder_enabled: event.reminder_enabled || false,
      reminder_hours: event.reminder_hours || 24,
      reminder_offsets: event.reminder_offsets || [],
      is_recurring: false,
      recurrence_pattern: "weekly",
      recurrence_count: 4,
      is_private: event.is_private || false,
      invited_player_ids: event.invited_player_ids || [],
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      event_type: "rehearsal",
      location: "",
      arrival_time: "",
      start_time: "",
      end_time: "",
      arrival_tbc: false,
      start_tbc: false,
      end_tbc: false,
      status: "confirmed",
      reminder_enabled: false,
      reminder_hours: 24,
      reminder_offsets: [],
      is_recurring: false,
      recurrence_pattern: "weekly",
      recurrence_count: 4,
      is_private: false,
      invited_player_ids: [],
    });
  };

  // Get status badge styling
  const getStatusBadge = (status) => {
    switch (status) {
      case 'provisional':
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30"><AlertCircle className="w-3 h-3 mr-1" />Provisional</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30 line-through"><XCircle className="w-3 h-3 mr-1" />Cancelled</Badge>;
      case 'confirmed':
      default:
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30"><CheckCircle className="w-3 h-3 mr-1" />Confirmed</Badge>;
    }
  };

  // Get events for selected date
  const eventsOnSelectedDate = events.filter(event => 
    isSameDay(new Date(event.start_time), selectedDate)
  );

  // Get dates that have events for calendar highlighting
  const eventDates = events.map(e => new Date(e.start_time));

  // Iter-66f → iter-66g: QuickRsvp promoted to /components/QuickRsvp.jsx
  // for reuse on the Dashboard. Local handler kept for optimistic updates.
  const onRsvpChange = (eventId, status) => {
    setEvents((prev) => prev.map((e) => (e.id === eventId ? { ...e, my_status: status } : e)));
  };

  // Iter-67: pull-to-refresh on mobile
  const { pullDistance, phase, threshold } = usePullToRefresh(fetchEvents);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading events...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <PullToRefreshIndicator pullDistance={pullDistance} phase={phase} threshold={threshold} />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <p className="overline mb-1">Schedule</p>
          <h1 className="text-3xl font-heading font-bold tracking-tight">Events</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage rehearsals and concerts</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap w-full sm:w-auto">
          {/* iter-33: Upcoming / Past toggle */}
          <div
            className="inline-flex rounded-lg border border-white/[0.06] bg-secondary/50 p-0.5 h-10"
            data-testid="events-time-filter"
            role="tablist"
          >
            <button
              type="button"
              role="tab"
              aria-selected={timeFilter === "upcoming"}
              onClick={() => setTimeFilter("upcoming")}
              className={`px-3 sm:px-4 text-sm rounded-md transition-colors ${timeFilter === "upcoming" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="events-tab-upcoming"
            >
              Upcoming
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={timeFilter === "past"}
              onClick={() => setTimeFilter("past")}
              className={`px-3 sm:px-4 text-sm rounded-md transition-colors ${timeFilter === "past" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="events-tab-past"
            >
              Past
            </button>
          </div>

          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-40 bg-secondary/50 border-white/[0.06] h-10" data-testid="event-filter-select">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Events</SelectItem>
              <SelectItem value="rehearsal">Rehearsals</SelectItem>
              <SelectItem value="concert">Concerts</SelectItem>
              <SelectItem value="contest" data-testid="filter-option-contest">Contests</SelectItem>
              {canSeeCommitteeMeetings && (
                <SelectItem value="committee_meeting" data-testid="filter-option-committee-meeting">
                  Committee Meetings
                </SelectItem>
              )}
            </SelectContent>
          </Select>
          
          {showAdminControls && (
            <>
              <Button 
                variant="outline" 
                className="border-white/[0.06] h-10"
                onClick={async () => {
                  try {
                    const res = await apiClient.get("/reports/events/upcoming.pdf", { responseType: "blob" });
                    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "upcoming-events.pdf";
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success("PDF downloaded");
                  } catch {
                    toast.error("Failed to download PDF");
                  }
                }}
                data-testid="download-upcoming-pdf-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export PDF
              </Button>
              <Button 
                variant="outline" 
                className="border-white/[0.06] h-10"
                onClick={async () => {
                  try {
                    const res = await apiClient.get("/reports/events/upcoming.xlsx", { responseType: "blob" });
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = "upcoming-events.xlsx";
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success("Excel downloaded");
                  } catch {
                    toast.error("Failed to download Excel");
                  }
                }}
                data-testid="download-upcoming-excel-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Export Excel
              </Button>
              <Button 
                variant="outline" 
                className="border-white/[0.06] h-10"
                onClick={() => setImportDialogOpen(true)}
                data-testid="import-events-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Import CSV
              </Button>
              <Dialog open={dialogOpen} onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) {
                  setEditingEvent(null);
                  resetForm();
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-primary text-primary-foreground btn-glow h-10" data-testid="create-event-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Event
                  </Button>
                </DialogTrigger>
              <DialogContent className="glass border-white/[0.06] max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl">
                    {editingEvent ? "Edit Event" : "Create New Event"}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-5 mt-4">
                  {/* 1. Event Title */}
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Event Title</Label>
                    <Input
                      data-testid="event-title-input"
                      placeholder="e.g., Weekly Rehearsal"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="bg-secondary/50 border-white/[0.06] h-10"
                      required
                    />
                  </div>
                  
                  {/* 2. Event Type & 3. Status - Side by side */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Event Type</Label>
                      <Select 
                        value={formData.event_type || "rehearsal"} 
                        onValueChange={(value) => setFormData({ ...formData, event_type: value })}
                      >
                        <SelectTrigger className="bg-secondary/50 border-white/[0.06] h-10" data-testid="event-type-select">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="rehearsal">Rehearsal</SelectItem>
                          <SelectItem
                            value="rehearsal_sectional"
                            data-testid="event-type-option-sectional"
                          >
                            Rehearsal — Sectional
                          </SelectItem>
                          <SelectItem value="concert">Concert</SelectItem>
                          <SelectItem value="contest" data-testid="event-type-option-contest">Contest</SelectItem>
                          {canSeeCommitteeMeetings && (
                            <SelectItem
                              value="committee_meeting"
                              data-testid="event-type-option-committee-meeting"
                            >
                              Committee Meeting
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Status</Label>
                      <Select 
                        value={formData.status || "confirmed"} 
                        onValueChange={(value) => setFormData({ ...formData, status: value })}
                      >
                        <SelectTrigger className="bg-secondary/50 border-white/[0.06] h-10" data-testid="event-status-select">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="confirmed">
                            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> Confirmed</span>
                          </SelectItem>
                          <SelectItem value="provisional">
                            <span className="flex items-center gap-2"><AlertCircle className="w-4 h-4 text-yellow-400" /> Provisional</span>
                          </SelectItem>
                          <SelectItem value="cancelled">
                            <span className="flex items-center gap-2"><XCircle className="w-4 h-4 text-red-400" /> Cancelled</span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* iter-159: sectional-only player picker */}
                  {formData.event_type === "rehearsal_sectional" && (
                    <InvitedPlayersPicker
                      value={formData.invited_player_ids || []}
                      onChange={(ids) =>
                        setFormData({ ...formData, invited_player_ids: ids })
                      }
                    />
                  )}

                  {/* 4. Date - Single date picker */}
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Date</Label>
                    <Input
                      data-testid="event-date-input"
                      type="date"
                      value={formData.start_time ? formData.start_time.split('T')[0] : ''}
                      onChange={(e) => {
                        const date = e.target.value;
                        const startTime = formData.start_time ? formData.start_time.split('T')[1] || '19:00' : '19:00';
                        const endTime = formData.end_time ? formData.end_time.split('T')[1] || '21:00' : '21:00';
                        const arrivalTime = formData.arrival_time ? formData.arrival_time.split('T')[1] || '' : '';
                        setFormData({ 
                          ...formData, 
                          start_time: date ? `${date}T${startTime}` : '',
                          end_time: date ? `${date}T${endTime}` : '',
                          arrival_time: arrivalTime ? `${date}T${arrivalTime}` : ''
                        });
                      }}
                      className="bg-secondary/50 border-white/[0.06] h-10"
                      required
                    />
                  </div>

                  {/* 5, 6, 7. Time Fields - Arrival, Start, Finish with TBC checkboxes */}
                  <div className="grid grid-cols-3 gap-3">
                    {/* Arrival Time */}
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                        <UserCheck className="w-3 h-3 text-orange-400" />
                        Arrival
                      </Label>
                      <Input
                        data-testid="event-arrival-input"
                        type="time"
                        value={formData.arrival_tbc ? '' : (formData.arrival_time ? formData.arrival_time.split('T')[1]?.substring(0, 5) || '' : '')}
                        onChange={(e) => {
                          const date = formData.start_time ? formData.start_time.split('T')[0] : '';
                          setFormData({ 
                            ...formData, 
                            arrival_time: e.target.value && date ? `${date}T${e.target.value}` : '',
                            arrival_tbc: false
                          });
                        }}
                        className="bg-secondary/50 border-white/[0.06] h-10"
                        disabled={formData.arrival_tbc}
                        placeholder={formData.arrival_tbc ? 'TBC' : ''}
                      />
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <Checkbox
                          checked={formData.arrival_tbc}
                          onCheckedChange={(checked) => setFormData({ ...formData, arrival_tbc: checked, arrival_time: checked ? '' : formData.arrival_time })}
                          className="h-3.5 w-3.5 border-white/20"
                          data-testid="arrival-tbc-checkbox"
                        />
                        <span className="text-[10px] text-muted-foreground">TBC</span>
                      </label>
                    </div>

                    {/* Start Time */}
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Start Time</Label>
                      <Input
                        data-testid="event-start-input"
                        type="time"
                        value={formData.start_tbc ? '' : (formData.start_time ? formData.start_time.split('T')[1]?.substring(0, 5) || '' : '')}
                        onChange={(e) => {
                          const date = formData.start_time ? formData.start_time.split('T')[0] : '';
                          setFormData({ 
                            ...formData, 
                            start_time: date ? `${date}T${e.target.value}` : '',
                            start_tbc: false
                          });
                        }}
                        className="bg-secondary/50 border-white/[0.06] h-10"
                        disabled={formData.start_tbc}
                        required={!formData.start_tbc}
                      />
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <Checkbox
                          checked={formData.start_tbc}
                          onCheckedChange={(checked) => {
                            const date = formData.start_time ? formData.start_time.split('T')[0] : '';
                            setFormData({ 
                              ...formData, 
                              start_tbc: checked,
                              start_time: checked ? (date ? `${date}T00:00` : '') : formData.start_time
                            });
                          }}
                          className="h-3.5 w-3.5 border-white/20"
                          data-testid="start-tbc-checkbox"
                        />
                        <span className="text-[10px] text-muted-foreground">TBC</span>
                      </label>
                    </div>

                    {/* Finish Time */}
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Finish Time</Label>
                      <Input
                        data-testid="event-end-input"
                        type="time"
                        value={formData.end_tbc ? '' : (formData.end_time ? formData.end_time.split('T')[1]?.substring(0, 5) || '' : '')}
                        onChange={(e) => {
                          const date = formData.start_time ? formData.start_time.split('T')[0] : '';
                          setFormData({ 
                            ...formData, 
                            end_time: date ? `${date}T${e.target.value}` : '',
                            end_tbc: false
                          });
                        }}
                        className="bg-secondary/50 border-white/[0.06] h-10"
                        disabled={formData.end_tbc}
                        required={!formData.end_tbc}
                      />
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <Checkbox
                          checked={formData.end_tbc}
                          onCheckedChange={(checked) => {
                            const date = formData.start_time ? formData.start_time.split('T')[0] : '';
                            setFormData({ 
                              ...formData, 
                              end_tbc: checked,
                              end_time: checked ? (date ? `${date}T23:59` : '') : formData.end_time
                            });
                          }}
                          className="h-3.5 w-3.5 border-white/20"
                          data-testid="end-tbc-checkbox"
                        />
                        <span className="text-[10px] text-muted-foreground">TBC</span>
                      </label>
                    </div>
                  </div>
                  
                  {/* 8. Location */}
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Location</Label>
                    <Input
                      data-testid="event-location-input"
                      placeholder="e.g., Band Hall, 123 Main Street"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="bg-secondary/50 border-white/[0.06] h-10"
                      required
                    />
                  </div>
                  
                  {/* 9. Description */}
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Details</Label>
                    <Textarea
                      data-testid="event-description-input"
                      placeholder="Add any additional details, dress code, programme notes..."
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="bg-secondary/50 border-white/[0.06] min-h-[80px] resize-none"
                    />
                  </div>
                  
                  {/* 10. Recurring Event Settings - Only show when creating new event */}
                  {!editingEvent && (
                    <div className="space-y-4 pt-4 border-t border-white/[0.06]">
                      <div className="flex items-center justify-between">
                        <div className="space-y-0.5">
                          <Label className="flex items-center gap-2 text-sm">
                            <Repeat className="w-4 h-4 text-blue-400" />
                            Recurring Event
                          </Label>
                          <p className="text-xs text-muted-foreground">
                            Create multiple events at regular intervals
                          </p>
                        </div>
                        <Switch
                          checked={formData.is_recurring}
                          onCheckedChange={(checked) => setFormData({ ...formData, is_recurring: checked })}
                          data-testid="recurring-switch"
                        />
                      </div>
                      
                      {formData.is_recurring && (
                        <div className="space-y-4 pl-6 animate-fade-in">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Repeat</Label>
                              <Select 
                                value={formData.recurrence_pattern} 
                                onValueChange={(value) => setFormData({ ...formData, recurrence_pattern: value })}
                              >
                                <SelectTrigger className="bg-secondary/50 border-white/[0.06] h-10" data-testid="recurrence-pattern-select">
                                  <SelectValue placeholder="Select pattern" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="weekly">Weekly</SelectItem>
                                  <SelectItem value="biweekly">Every 2 Weeks</SelectItem>
                                  <SelectItem value="monthly">Monthly</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Number of Events</Label>
                              <Select 
                                value={String(formData.recurrence_count)} 
                                onValueChange={(value) => setFormData({ ...formData, recurrence_count: parseInt(value) })}
                              >
                                <SelectTrigger className="bg-secondary/50 border-white/[0.06] h-10" data-testid="recurrence-count-select">
                                  <SelectValue placeholder="Select count" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="4">4 events</SelectItem>
                                  <SelectItem value="8">8 events</SelectItem>
                                  <SelectItem value="12">12 events</SelectItem>
                                  <SelectItem value="16">16 events</SelectItem>
                                  <SelectItem value="24">24 events</SelectItem>
                                  <SelectItem value="52">52 events (1 year)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            This will create {formData.recurrence_count} {formData.event_type}s, 
                            {formData.recurrence_pattern === "weekly" && " one per week"}
                            {formData.recurrence_pattern === "biweekly" && " one every two weeks"}
                            {formData.recurrence_pattern === "monthly" && " one per month"}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* 11. Email Reminder Settings */}
                  <div className="space-y-4 pt-4 border-t border-white/[0.06]">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label className="flex items-center gap-2 text-sm">
                          <Bell className="w-4 h-4 text-primary" />
                          Email Reminders
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Send reminders to players who responded Yes or Maybe
                        </p>
                      </div>
                      <Switch
                        checked={formData.reminder_enabled}
                        onCheckedChange={(checked) => setFormData({ ...formData, reminder_enabled: checked })}
                        data-testid="reminder-enabled-switch"
                      />
                    </div>
                    
                    {formData.reminder_enabled && (
                      <div className="space-y-2 pl-6 animate-fade-in">
                        <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          When to send reminders (pick as many as you like)
                        </Label>
                        {(() => {
                          // iter-107k: render the offset catalogue as 3
                          // category columns. Selection state is the
                          // `reminder_offsets: string[]` array.
                          if (!reminderCatalogue.length) {
                            return <p className="text-xs text-muted-foreground">Loading options…</p>;
                          }
                          const grouped = {
                            far_ahead: { label: "Far ahead", items: [] },
                            close: { label: "Close to the event", items: [] },
                            smart: { label: "Smart timing", items: [] },
                          };
                          for (const opt of reminderCatalogue) {
                            (grouped[opt.category]?.items || []).push(opt);
                          }
                          const selected = new Set(formData.reminder_offsets || []);
                          const toggle = (code) => {
                            const next = new Set(selected);
                            if (next.has(code)) next.delete(code);
                            else next.add(code);
                            setFormData({ ...formData, reminder_offsets: [...next] });
                          };
                          return (
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                              {Object.entries(grouped).map(([key, group]) => (
                                <div key={key} className="space-y-1.5">
                                  <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80">
                                    {group.label}
                                  </p>
                                  {group.items.map((opt) => (
                                    <label
                                      key={opt.code}
                                      className="flex items-center gap-2 text-xs cursor-pointer rounded p-1.5 hover:bg-white/5"
                                      data-testid={`reminder-offset-${opt.code}`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={selected.has(opt.code)}
                                        onChange={() => toggle(opt.code)}
                                      />
                                      <span>{opt.label}</span>
                                    </label>
                                  ))}
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                        <p className="text-xs text-muted-foreground">
                          New events pre-tick sensible defaults per event type. Non-responders also get an auto-reminder 7 days before.
                        </p>
                        {editingEvent && (
                          <div className="pt-3 mt-3 border-t border-white/[0.06]">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={sendingTestReminder}
                              onClick={handleSendTestReminder}
                              data-testid="send-test-reminder-btn"
                              className="text-xs"
                            >
                              <Bell className="w-3.5 h-3.5 mr-1.5" />
                              {sendingTestReminder ? "Sending…" : "Send sample reminder to me"}
                            </Button>
                            <p className="text-[11px] text-muted-foreground mt-1.5">
                              Fires a one-off preview to <strong>{user?.email}</strong> using
                              the current event details + programme. Members are not emailed.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Iter-71: Private event toggle. Functions identically inside the
                      app — the only effect is on the public concert embed widget,
                      where the title, time and location are hidden and the entry
                      is shown as "Private event" alongside the date. */}
                  <div className="space-y-2 pt-4 border-t border-white/[0.06]">
                    <div className="flex items-center justify-between">
                      <div className="space-y-0.5">
                        <Label className="flex items-center gap-2 text-sm">
                          <Lock className="w-4 h-4 text-primary" />
                          Private event
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Public website widget will show only the date and the label &quot;Private event&quot; — title, time and location are hidden. No effect on members&apos; in-app view.
                        </p>
                      </div>
                      <Switch
                        checked={!!formData.is_private}
                        onCheckedChange={(checked) => setFormData({ ...formData, is_private: checked })}
                        data-testid="is-private-switch"
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)} className="h-10">
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-primary text-primary-foreground h-10" data-testid="save-event-btn">
                      {editingEvent ? "Update Event" : "Create Event"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            
            <ImportDialog
              open={importDialogOpen}
              onOpenChange={setImportDialogOpen}
              importType="events"
              onSuccess={fetchEvents}
            />
            </>
          )}
        </div>
      </div>

      {/* Main Content — stacked: Upcoming → Date-selected → Calendar */}
      <div className="space-y-6">

        {/* 1. ALL UPCOMING EVENTS (top) */}
        <Card className="glass-card">
          <CardHeader className="border-b border-white/5 pb-4">
            <CardTitle className="font-heading text-lg">All Upcoming Events</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {events.length > 0 ? (
              <div className="space-y-3">
                {events.map((event) => {
                  const cancelled = (event.status || "").toLowerCase() === "cancelled";
                  return (
                  <div
                    key={event.id}
                    className={`flex items-center justify-between gap-3 p-3 rounded-lg bg-secondary/20 hover:bg-secondary/30 transition-colors cursor-pointer ${cancelled ? 'opacity-60' : ''}`}
                    onClick={() => navigate(`/events/${event.id}`)}
                    data-testid={`event-list-item-${event.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        event.event_type === 'concert' ? 'bg-velvet-red'
                          : event.event_type === 'contest' ? 'bg-purple-400'
                          : event.event_type === 'committee_meeting' ? 'bg-amber-400'
                          : event.event_type === 'rehearsal_sectional' ? 'bg-cyan-400'
                          : 'bg-blue-400'
                      }`} />
                      <div className="min-w-0">
                        <p className={`font-medium text-sm truncate ${cancelled ? 'line-through text-muted-foreground' : ''}`}>
                          {event.title}
                          {event.event_type === 'rehearsal_sectional' && (
                            <span
                              className="ml-2 inline-flex items-center rounded-full bg-cyan-500/15 text-cyan-300 border border-cyan-400/30 text-[10px] px-2 py-0.5 align-middle"
                              data-testid={`sectional-badge-${event.id}`}
                            >
                              Sectional
                            </span>
                          )}
                          {event.event_type === 'contest' && (
                            <span
                              className="ml-2 inline-flex items-center rounded-full bg-purple-500/15 text-purple-300 border border-purple-400/30 text-[10px] px-2 py-0.5 align-middle"
                              data-testid={`contest-badge-${event.id}`}
                            >
                              Contest
                            </span>
                          )}
                          {event.event_type === 'committee_meeting' && (
                            <span
                              className="ml-2 inline-flex items-center rounded-full bg-amber-500/15 text-amber-300 border border-amber-400/30 text-[10px] px-2 py-0.5 align-middle"
                              data-testid={`committee-badge-${event.id}`}
                            >
                              Committee
                            </span>
                          )}
                          {cancelled && (
                            <span
                              className="ml-2 inline-flex items-center rounded-md bg-red-500/15 text-red-500 border border-red-500/40 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 align-middle"
                              data-testid={`cancelled-stamp-${event.id}`}
                            >
                              Cancelled
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(event.start_time), "MMM d, yyyy 'at' h:mm a")}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <QuickRsvp event={event} compact hidden={showAdminControls || !!user?.is_info_only || cancelled} onChange={onRsvpChange} />
                      <Badge variant="outline" className="text-xs hidden sm:inline-flex">
                        {event.location}
                      </Badge>
                    </div>
                  </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">No events found</p>
            )}
          </CardContent>
        </Card>

        {/* 2. EVENTS ON SELECTED DATE (middle) */}
        <Card className="glass-card">
          <CardHeader className="border-b border-white/5 pb-4">
            <CardTitle className="font-heading text-lg">
              Events on {format(selectedDate, "MMMM d, yyyy")}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {eventsOnSelectedDate.length > 0 ? (
              <div className="space-y-4">
                {eventsOnSelectedDate.map((event) => (
                  <div
                    key={event.id}
                    className="flex flex-wrap items-start gap-4 p-4 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/events/${event.id}`)}
                    data-testid={`event-card-${event.id}`}
                  >
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      event.event_type === 'concert' ? 'bg-velvet-red/20'
                        : event.event_type === 'contest' ? 'bg-purple-500/20'
                        : event.event_type === 'committee_meeting' ? 'bg-amber-500/20'
                        : 'bg-blue-500/20'
                    } ${event.status === 'cancelled' ? 'opacity-50' : ''}`}>
                      {event.event_type === 'concert' ? (
                        <Music2 className="w-6 h-6 text-velvet-red" />
                      ) : event.event_type === 'contest' ? (
                        <Music2 className="w-6 h-6 text-purple-400" />
                      ) : event.event_type === 'committee_meeting' ? (
                        <CalendarIcon className="w-6 h-6 text-amber-400" />
                      ) : (
                        <CalendarIcon className="w-6 h-6 text-blue-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className={`font-medium ${event.status === 'cancelled' ? 'line-through text-muted-foreground' : ''}`}>{event.title}</h3>
                        <Badge variant="outline" className={`text-xs ${
                          event.event_type === 'concert'
                            ? 'border-velvet-red/30 text-velvet-red'
                            : event.event_type === 'contest'
                            ? 'border-purple-400/30 text-purple-300'
                            : event.event_type === 'committee_meeting'
                            ? 'border-amber-400/30 text-amber-300'
                            : 'border-blue-400/30 text-blue-400'
                        }`}>
                          {event.event_type === 'committee_meeting' ? 'committee meeting' : event.event_type}
                        </Badge>
                        {event.status && event.status !== 'confirmed' && getStatusBadge(event.status)}
                      </div>
                      {event.description && (
                        <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                          {(() => {
                            // Strip HTML tags + decode common entities so the
                            // rich-text description renders as a clean
                            // single-paragraph preview. Full HTML is rendered
                            // on the Event Detail page via DOMPurify.
                            const tmp = document.createElement('div');
                            tmp.innerHTML = event.description;
                            const text = (tmp.textContent || tmp.innerText || '').trim();
                            return text.length > 220 ? text.slice(0, 220) + '…' : text;
                          })()}
                        </p>
                      )}
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                        {event.arrival_time && (
                          <span className="flex items-center gap-1 text-orange-400">
                            <UserCheck className="w-3 h-3" />
                            Arrive: {format(new Date(event.arrival_time), "h:mm a")}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {format(new Date(event.start_time), "h:mm a")} - {format(new Date(event.end_time), "h:mm a")}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {event.location}
                        </span>
                      </div>
                    </div>
                    {showAdminControls ? (
                      <div className="flex gap-2 w-full sm:w-auto justify-end" onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(event)}
                          data-testid={`edit-event-${event.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDelete(event.id)}
                          data-testid={`delete-event-${event.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <QuickRsvp
                        event={event}
                        hidden={!!user?.is_info_only}
                        onChange={onRsvpChange}
                        className="w-full sm:w-auto justify-end"
                      />
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <CalendarIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No events on this date</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 3. CALENDAR (bottom) */}
        <Card className="glass-card">
          <CardHeader className="border-b border-white/5 pb-4">
            <CardTitle className="font-heading text-lg">Calendar</CardTitle>
          </CardHeader>
          <CardContent className="p-2 sm:p-4 overflow-x-auto">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              className="rounded-md mx-auto"
              modifiers={{ hasEvent: eventDates }}
              modifiersStyles={{
                hasEvent: {
                  backgroundColor: 'hsl(45, 93%, 47%, 0.2)',
                  borderRadius: '50%'
                }
              }}
            />
          </CardContent>
        </Card>

      </div>

    </div>
  );
};

export default Events;
